import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import "./theme.css";
import { AuthProvider, RequireAuth } from "./lib/auth";
import Login from "./pages/Login";
import CommandCenter from "./pages/CommandCenter";
import BrandVault from "./pages/BrandVault";
import BrandBrain from "./pages/BrandBrain";
import ReviewQueue from "./pages/ReviewQueue";
import Interview from "./pages/Interview";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import ProductBrain from "./pages/ProductBrain";
import ConflictCenter from "./pages/ConflictCenter";
import Audiences from "./pages/Audiences";
import AudienceBrain from "./pages/AudienceBrain";
import RetrievalExplorer from "./pages/RetrievalExplorer";
// 2026-10-26 — legacy "Generate brand-safe copy" (CreativeStudio.jsx)
// deleted per Roger's "retired = deleted" rule. The only copy path is
// the governed campaign → Creative pipeline (CreativeStudioPanel on
// each campaign). Any old `/brand-os/studio` link now redirects to the
// Campaigns list so operators land on a governed entry point.
import AssetLibrary from "./pages/AssetLibrary";
import GovernanceDashboard from "./pages/GovernanceDashboard";
import Campaigns from "./pages/Campaigns";
import CampaignDetail from "./pages/CampaignDetail";
import ChannelConnectionsAdmin from "./pages/ChannelConnectionsAdmin";
import BrandConnect from "./pages/BrandConnect";
import FacebookOAuthCallback from "./pages/FacebookOAuthCallback";
import PublishCalendar from "./pages/PublishCalendar";
import PublishCompose from "./pages/PublishCompose";
import PublishFanOut from "./pages/PublishFanOut";
import PublishFanOutRollup from "./pages/PublishFanOutRollup";
import PublishQueue from "./pages/PublishQueue";
import PublishReceiptDetail from "./pages/PublishReceiptDetail";
import PublishScheduleWeek from "./pages/PublishScheduleWeek";
import AdapterCallExplorer from "./pages/AdapterCallExplorer";
import AcceptanceDryRun from "./pages/AcceptanceDryRun";
import PublishHealth from "./pages/PublishHealth";
import TikTokDirectPost from "./pages/TikTokDirectPost";
import TikTokResults from "./pages/TikTokResults";
import Settings from "./pages/Settings";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import Onboarding from "./pages/Onboarding";
import FoundationOverview from "./pages/FoundationOverview";
import KnowledgeOverview from "./pages/KnowledgeOverview";
import CreateOverview from "./pages/CreateOverview";
import OperateOverview from "./pages/OperateOverview";
import InsightsOverview from "./pages/InsightsOverview";
import ChannelInsights from "./pages/ChannelInsights";
import ReviewWalkthrough from "./pages/ReviewWalkthrough";
import AdAccounts from "./pages/AdAccounts";
import TruthGapDashboard from "./pages/TruthGapDashboard";
import GoldenPath from "./pages/GoldenPath";
import WaitlistAdmin from "./pages/WaitlistAdmin";
import EmailPreview from "./pages/EmailPreview";
import FounderInbox from "./pages/FounderInbox";
import SocialInbox from "./pages/SocialInbox";
import AutonomyDial from "./pages/AutonomyDial";
import GuardianVaultUI from "./pages/GuardianVaultUI";
import Planner from "./pages/Planner";
import LearningLoop from "./pages/LearningLoop";
import FounderVideos from "./pages/FounderVideos";
import ImportVideo from "./pages/ImportVideo";
import AdCreationWizard from "./pages/AdCreationWizard";
import AdsInsights from "./pages/AdsInsights";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import YouTubeOAuthCallback from "./pages/YouTubeOAuthCallback";
import TikTokOAuthCallback from "./pages/TikTokOAuthCallback";
import PinterestOAuthCallback from "./pages/PinterestOAuthCallback";
import NotFound from "./pages/NotFound";

function Protected({ children }) {
  return <RequireAuth>{children}</RequireAuth>;
}

export default function BrandOSApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="login" element={<Login />} />
        {/* P7 — public customer signup + verification */}
        <Route path="signup" element={<Signup />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="auth/magic-link" element={<MagicLinkVerify />} />
        <Route path="auth/callback" element={<GoogleAuthCallback />} />
        <Route index element={<Protected><CommandCenter /></Protected>} />
        <Route path="vault" element={<Protected><BrandVault /></Protected>} />
        <Route path="brand-brain" element={<Protected><BrandBrain /></Protected>} />
        <Route path="review-queue" element={<Protected><ReviewQueue /></Protected>} />
        <Route path="interview" element={<Protected><Interview /></Protected>} />
        <Route path="products" element={<Protected><Products /></Protected>} />
        <Route path="products/:productId" element={<Protected><ProductDetail /></Protected>} />
        <Route path="product-brain" element={<Protected><ProductBrain /></Protected>} />
        <Route path="conflicts" element={<Protected><ConflictCenter /></Protected>} />
        <Route path="audiences" element={<Protected><Audiences /></Protected>} />
        <Route path="audience-brain" element={<Protected><AudienceBrain /></Protected>} />
        <Route path="retrieval" element={<Protected><RetrievalExplorer /></Protected>} />
        {/* 2026-10-26 — old legacy copy page is gone; redirect any
            bookmark / in-app `/brand-os/studio` link to the governed
            Campaigns list (users pick a campaign, open its Creative
            tab, and generate copy through the governed pipeline). */}
        <Route path="studio" element={<Navigate to="/brand-os/campaigns" replace />} />
        <Route path="studio/*" element={<Navigate to="/brand-os/campaigns" replace />} />
        <Route path="library" element={<Protected><AssetLibrary /></Protected>} />
        <Route path="governance" element={<Protected><GovernanceDashboard /></Protected>} />
        <Route path="campaigns" element={<Protected><Campaigns /></Protected>} />
        <Route path="campaigns/:campaignId" element={<Protected><CampaignDetail /></Protected>} />
        <Route path="admin/channel-connections" element={<Protected><ChannelConnectionsAdmin /></Protected>} />
        <Route path="connect" element={<Protected><BrandConnect /></Protected>} />
        <Route path="channels/connect-facebook" element={<Protected><FacebookOAuthCallback /></Protected>} />
        <Route path="channels/connect-youtube" element={<Protected><YouTubeOAuthCallback /></Protected>} />
        <Route path="channels/connect-tiktok" element={<Protected><TikTokOAuthCallback /></Protected>} />
        <Route path="channels/connect-pinterest" element={<Protected><PinterestOAuthCallback /></Protected>} />
        <Route path="publish/queue" element={<Protected><PublishQueue /></Protected>} />
        <Route path="publish/health" element={<Protected><PublishHealth /></Protected>} />
        <Route path="publish/tiktok" element={<Protected><TikTokDirectPost /></Protected>} />
        <Route path="publish/tiktok/results" element={<Protected><TikTokResults /></Protected>} />
        <Route path="publish/receipts/:receiptId" element={<Protected><PublishReceiptDetail /></Protected>} />
        <Route path="publish/calendar" element={<Protected><PublishCalendar /></Protected>} />
        <Route path="publish/new" element={<Protected><PublishCompose /></Protected>} />
        <Route path="publish/fan-out" element={<Protected><PublishFanOut /></Protected>} />
        <Route path="publish/fan-out/:fanOutId" element={<Protected><PublishFanOutRollup /></Protected>} />
        <Route path="publish/schedule-week" element={<Protected><PublishScheduleWeek /></Protected>} />
        <Route path="publish/adapter-calls" element={<Protected><AdapterCallExplorer /></Protected>} />
        <Route path="publish/acceptance-dry-run" element={<Protected><AcceptanceDryRun /></Protected>} />
        <Route path="settings" element={<Protected><Settings /></Protected>} />
        {/* Section-header landing pages (SR-1, 2026-08-19) */}
        <Route path="foundation" element={<Protected><FoundationOverview /></Protected>} />
        <Route path="knowledge" element={<Protected><KnowledgeOverview /></Protected>} />
        <Route path="create" element={<Protected><CreateOverview /></Protected>} />
        <Route path="create/import-video" element={<Protected><ImportVideo /></Protected>} />
        <Route path="operate" element={<Protected><OperateOverview /></Protected>} />
        <Route path="insights" element={<Protected><InsightsOverview /></Protected>} />
        <Route path="channel-insights" element={<Protected><ChannelInsights /></Protected>} />
        <Route path="review-walkthrough" element={<Protected><ReviewWalkthrough /></Protected>} />
        <Route path="ads" element={<Protected><AdAccounts /></Protected>} />
        <Route path="ads/create" element={<Protected><AdCreationWizard /></Protected>} />
        <Route path="ads/insights" element={<Protected><AdsInsights /></Protected>} />
        <Route path="truth-gap" element={<Protected><TruthGapDashboard /></Protected>} />
        <Route path="golden-path" element={<Protected><GoldenPath /></Protected>} />
        <Route path="waitlist" element={<Protected><WaitlistAdmin /></Protected>} />
        <Route path="waitlist/email-preview" element={<Protected><EmailPreview /></Protected>} />
        <Route path="inbox" element={<Protected><FounderInbox /></Protected>} />
        <Route path="social-inbox" element={<Protected><SocialInbox /></Protected>} />
        <Route path="autonomy" element={<Protected><AutonomyDial /></Protected>} />
        <Route path="planner" element={<Protected><Planner /></Protected>} />
        <Route path="learning" element={<Protected><LearningLoop /></Protected>} />
        <Route path="vault/guardian" element={<Protected><GuardianVaultUI /></Protected>} />
        <Route path="videos" element={<Protected><FounderVideos /></Protected>} />
        <Route path="vault/bulk-import" element={<Navigate to="/brand-os/vault" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}
